import { useInfiniteQuery } from "@tanstack/react-query";
import { useCallback, useState } from "react";

import { formatDate } from "@/components/FormattedDate/FormattedDate";
import type { ModalBaseProps } from "@/components/Modal/Modal";
import { Modal } from "@/components/Modal/Modal";
import { TicketStatus } from "@/components/Ticket/TicketStatus";
import { Button } from "@/design-system/Button";
import { Inline } from "@/design-system/Inline";
import { Link } from "@/design-system/Link";
import { RatingBadge } from "@/design-system/RatingBadge";
import { Spinner } from "@/design-system/Spinner";
import { Stack } from "@/design-system/Stack";
import { useOnIntersection } from "@/hooks/useOnIntersection";
import { useSlug } from "@/hooks/useSlug";
import type { TicketHistoryType } from "@/queries/tickets/constants";
import { useTicketQueries } from "@/queries/tickets/queryOptions";
import { routes } from "@/routes";
import { useTranslation } from "@/translations";

const USER_TICKET_HISTORY = "users" as const satisfies TicketHistoryType;
const ADDRESS_TICKET_HISTORY = "addresses" as const satisfies TicketHistoryType;
type TicketHistoryModalProps = ModalBaseProps & {
  ticketId: string;
};
export function TicketHistoryModal({ ticketId, isOpened, onOpenChange }: TicketHistoryModalProps): React.ReactNode {
  const slug = useSlug();
  const [ticketHistoryType, setTicketHistoryType] = useState<TicketHistoryType>(USER_TICKET_HISTORY);
  const { i18n, t } = useTranslation();
  const ticketQueries = useTicketQueries();

  const {
    data: ticketHistoryData,
    hasNextPage: hasMoreHistory,
    isLoading: isLoadingHistory,
    isFetchingNextPage: isLoadingMoreHistory,
    fetchNextPage: loadMoreHistory,
  } = useInfiniteQuery({
    ...ticketQueries.historyInfinite({ ticketId, historyType: ticketHistoryType }),
    enabled: ticketId !== undefined && isOpened,
  });
  const ticketHistory = {
    userTotal: ticketHistoryData?.pages[0].userTotal || 0,
    addressTotal: ticketHistoryData?.pages[0].addressTotal || 0,
    history: ticketHistoryData?.pages.flatMap((x) => x.history.items) || [],
  };
  const ref = useOnIntersection({
    threshold: 0,
    onIntersect: useCallback(() => {
      if (!isLoadingMoreHistory && hasMoreHistory) {
        void loadMoreHistory();
      }
    }, [isLoadingMoreHistory, hasMoreHistory, loadMoreHistory]),
  });

  return (
    <Modal.Root
      title={t("page.tickets.details.service-history.modal.title")}
      {...{
        isOpened,
        onOpenChange,
      }}
      height={680}
      isNested
    >
      <Modal.Body>
        <Stack>
          <Inline wrap={false}>
            <Button
              variant={ticketHistoryType === USER_TICKET_HISTORY ? "filled" : "soft"}
              size="sm"
              shape="start"
              onClick={() => setTicketHistoryType(USER_TICKET_HISTORY)}
            >
              {t("page.tickets.details.service-history.modal.tab.resident", {
                count: ticketHistory.userTotal,
              })}
            </Button>

            <Button
              variant={ticketHistoryType === ADDRESS_TICKET_HISTORY ? "filled" : "soft"}
              size="sm"
              shape="end"
              onClick={() => setTicketHistoryType(ADDRESS_TICKET_HISTORY)}
            >
              {t("page.tickets.details.service-history.modal.tab.address", {
                count: ticketHistory.addressTotal,
              })}
            </Button>
          </Inline>
          {isLoadingHistory && (
            <section className="my-space-md flex justify-center">
              <Spinner size="md" />
            </section>
          )}
          {ticketHistory.history.map((historyItem) => (
            <Link
              key={historyItem.id}
              href={routes.tickets.details({ slug, id: historyItem.id })}
              tone="default"
              underline="none"
              fullWidth
            >
              <span className="flex w-full flex-col gap-space-sm border-b border-border-default py-space-md pl-space-md hocus:bg-background-secondary">
                <Inline gap="sm" align="center" wrap={false}>
                  <span className="rounded-lg bg-background-brand-light px-space-sm py-space-xs text-body-emphasis text-text-primary">
                    {historyItem.id}
                  </span>
                  <TicketStatus
                    labelColor={historyItem.status.color}
                    description={historyItem.status.name}
                    variant="filled"
                  />
                  {!!historyItem.rating && <RatingBadge rating={historyItem.rating} />}
                </Inline>
                <span className="text-left text-body-emphasis wrap-break-word">{historyItem.title}</span>
                <span className="text-left text-overline">
                  {t("page.tickets.table.content.title", {
                    when: formatDate(i18n, "datetimeShort", historyItem.lastActivityAt),
                    who: historyItem.lastActivityBy?.fullName || historyItem.user.fullName,
                  })}
                </span>
              </span>
            </Link>
          ))}
          {hasMoreHistory && (
            <footer className="flex min-h-control-xl items-center justify-center p-space-md" ref={ref}>
              {isLoadingMoreHistory && <Spinner size="md" />}
            </footer>
          )}
        </Stack>
      </Modal.Body>
    </Modal.Root>
  );
}
